import React, { useState, useEffect, lazy, Suspense, useMemo, useCallback } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { IStore } from "@/redux/defaultStore";
import { useQueryClient } from "@tanstack/react-query";
import { useScopedGate, ENABLE_COCKPIT_V3_ACCESS_GATE_KEY } from "@/hooks/useScopedGate";
import { usePlayPageData, usePlayPageState } from "@/hooks/usePlayPageData";
import { useGetActiveChallenges } from "@/hooks/challenges/useGetActiveChallenges";
import { useGetUserProfile } from "@/hooks/useGetUserProfile";
import { useGetTransactionsPaginated } from "@/hooks/useGetTransactionsPaginated";
import { useGetGuildProgress } from "@/hooks/useGetGuildProgress";
import useGetGuildOwnerships from "@/hooks/useGetGuildOwnerships";
import useGetCurrentGuildTopMembers from "@/hooks/useGetCurrentGuildTopMembers";
import { useGetUserLeaderboardRank } from "@/hooks/useGetUserLeaderboardRank";
import { useGetOverwolfSummary } from "@/hooks/useGetOverwolfSummary";
import { CockpitDataProvider } from "@/contexts/play/CockpitDataContext";
import { PLAY_PAGE_TABS, PLAY_PAGE_FEATURES } from "@/config/playPageConfig";
import type { TabComponentProps, FormattedChallenge } from "@/types/play";
import { OWGameResponse } from "@devour/client";
import DevourTopNav from "@/components/DevourTopNav";
import CompactProfileBar from "@/components/CompactProfileBar";
import CompactTabNavigation from "@/components/CompactTabNavigation";
import ActiveMissionCard from "@/components/play/ActiveMissionCard";
import MobileBottomNav from "@/components/play/MobileBottomNav";
import CollapsiblePanel from "@/components/play/CollapsiblePanel";
import NetworkStatusIndicator from "@/components/play/NetworkStatusIndicator";
import InGameCompactView from "@/components/play/InGameCompactView";
// import LayoutSwitcher from '@/components/play/LayoutSwitcher'; // Temporarily hidden
import GameSearchInline from "@/components/play/GameSearchInline";
import GameSearchModal from "@/components/modals/GameSearchModal";
import GoVipLeaderboardsModal from "@/components/modals/GoVipLeaderboardsModal";
import GoVipLevelsModal from "@/components/modals/GoVipLevelsModal";
import LoginModal from "@/components/modals/LoginModal";
import LoadFuelModal from "@/components/modals/LoadFuelModal";
import ProfileCard from "@/components/shared/ProfileCard";
import StatsCard from "@/components/play/cards/StatsCard";
import TransactionHistoryCard from "@/components/play/cards/TransactionHistoryCard";
import MyQuestsCard from "@/components/play/cards/MyQuestsCard";
import GuildsLeaderboardCard from "@/components/play/cards/GuildsLeaderboardCard";
import DevourPlayChat from "@/components/play/cards/DevourPlayChat";
import { LastSessionCard } from "@/components/play/cards/LastSessionCard";
import { QuestProgressCard } from "@/components/play/cards/QuestProgressCard";
import { ActiveQuestsCard } from "@/components/play/cards/ActiveQuestsCard";
import RotatingActionCards from "@/components/play/cards/RotatingActionCards";
import CollapsibleDataCard from "@/components/play/CollapsibleDataCard";
import CockpitLoader from "@/components/play/CockpitLoader";
import ActionIcons from "@/components/play/ActionIcons";
import { FiTrendingUp } from "react-icons/fi";
import { useIsMobile } from "@/hooks/play/useIsMobile";
import { useDeviceContext } from "@/hooks/play/useDeviceContext";
import { useLayoutPreference, LayoutPreferenceProvider } from "@/contexts/play/LayoutPreferenceContext";
import { useNetworkState } from "@/hooks/useNetworkState";
import { useNetworkAwareRetry } from "@/hooks/useNetworkAwareRetry";
import { useGameBySlug } from "@/hooks/overwolf/useGameBySlug";
import getConfig from "@/utils/getConfig";
import { useGameSearchInfinite } from "@/hooks/overwolf/useGameSearchInfinite";
// Game page sections for dynamic content
import GameHeroSection from "@/components/games/pages/sections/GameHeroSection";
import GameQuestSection from "@/components/games/pages/sections/GameQuestSection";
import GameLeaderboardSection from "@/components/games/pages/sections/GameLeaderboardSection";
import GameNewsSection from "@/components/games/pages/sections/GameNewsSection";
import GameAboutSection from "@/components/games/pages/sections/GameAboutSection";
import GameSteamStatsSection from "@/components/games/pages/sections/GameSteamStatsSection";
import GuildsCockpitPage from "@/pages/GuildsCockpitPage";
import { useGameData } from "@/hooks/games/useGameData";
import "./CockpitV3.scss";
// Lazy load action icon pages
const RestaurantPage = lazy(() => import("@/pages/restaurants/RestaurantPage"));
const LootBoxPage = lazy(() => import("@/pages/LootBoxPage").then(module => ({ default: module.LootBoxPage })));
const CollectiblesPage = lazy(() => import("@/pages/CollectiblesPage").then(module => ({ default: module.CollectiblesPage })));

// Constants
const MAX_CHALLENGES_DISPLAY = 5;
const CHALLENGE_PLACEHOLDER_IMAGE = "https://cdn.devourgo.io/images/brand-challenge-placeholder.png";

// Lazy load tab components for better performance
const GamesTab = lazy(() => import("@/components/play/tabs/GamesTab"));
const GuildsTab = lazy(() => import("@/components/play/tabs/GuildsTab"));
const SharePlayTab = lazy(() => import("@/components/play/tabs/SharePlayTab"));
const InventoryTab = lazy(() => import("@/components/play/tabs/InventoryTab"));

// Tab components mapping with proper types
const TAB_COMPONENTS: Record<string, React.LazyExoticComponent<React.FC<TabComponentProps>>> = {
    games: GamesTab,
    guilds: GuildsTab,
    share: SharePlayTab,
    inventory: InventoryTab,
};

/**
 * Hero Section Component - Featured Challenge Display
 * Uses real BrandChallenges data for hero content
 */
const HeroSection: React.FC<{
    featuredChallenge: FormattedChallenge | null;
    challengesLoading: boolean;
}> = ({ featuredChallenge, challengesLoading }) => {
    if (challengesLoading) {
        return (
            <div className="hero-section hero-section--loading">
                <div className="hero-loading">Loading featured challenge...</div>
            </div>
        );
    }

    if (!featuredChallenge) {
        return (
            <div className="hero-section hero-section--empty">
                <div className="hero-empty">
                    <h2>No Active Challenges</h2>
                    <p>Check back soon for new brand challenges!</p>
                </div>
            </div>
        );
    }

    return (
        <div className="hero-section">
            {/* Challenge Background */}
            <div
                className="hero-background"
                style={{
                    backgroundImage: `url(${featuredChallenge.backgroundImageLarge || featuredChallenge.imageUrl})`,
                }}
            />

            {/* Challenge Overlay Content */}
            <div className="hero-overlay">
                <div className="hero-content">
                    <div className="hero-badge">FEATURED CHALLENGE</div>
                    <h1 className="hero-title">{featuredChallenge.name}</h1>
                    <p className="hero-description">{featuredChallenge.description}</p>

                    <div className="hero-meta">
                        <div className="hero-brand">
                            {featuredChallenge.brandLogo &&
                <img
                    src={featuredChallenge.brandLogo}
                    alt="Brand"
                    className="hero-brand-logo"
                />
                            }
                            <span className="hero-brand-name">Brand Challenge</span>
                        </div>
                        <div className="hero-status">
                            <span className="hero-status-dot" />
                            <span className="hero-status-text">ACTIVE</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

/**
 * User Profile Panel - Real Account Data
 * Uses actual user level, XP, avatar, fuel balance
 */
const UserProfilePanel: React.FC<{
    fuelBalance: string;
    accountLevel: any;
    currentUser: any;
    userProfile: any;
}> = ({ fuelBalance, accountLevel, currentUser, userProfile }) => {
    // Calculate XP progress
    const experienceGained = accountLevel?.experienceGainedAtThisLevel || 0;
    const experienceRequired = accountLevel?.experienceRequiredForNextLevel || 1;
    const totalExperience = experienceGained + experienceRequired;
    const xpPercentage = experienceGained / totalExperience * 100;

    return (
        <div className="user-profile-panel">
            <div className="profile-header">
                <div className="profile-avatar">
                    <img
                        src={userProfile?.user?.gravatar || currentUser?.user?.profilePhotoUrl || "/placeholder-avatar.jpg"}
                        alt="User Avatar"
                    />
                    <div className="profile-level">{accountLevel?.level || 1}</div>
                </div>
                <div className="profile-info">
                    <h3 className="profile-name">{userProfile?.user?.displayName || currentUser?.user?.displayName || "Player"}</h3>
                    <div className="profile-stats">
                        <span className="profile-fuel">💰 {fuelBalance} FUEL</span>
                    </div>
                </div>
            </div>

            <div className="profile-progress">
                <div className="progress-label">
                    <span>Level {accountLevel?.level || 1} Progress</span>
                    <span>{experienceGained.toLocaleString()} / {totalExperience.toLocaleString()} XP</span>
                </div>
                <div className="progress-bar">
                    <div
                        className="progress-fill"
                        style={{ width: `${Math.min(xpPercentage, 100)}%` }}
                    />
                </div>
            </div>
        </div>
    );
};

/**
 * Game Status Panel - Current Game Detection
 * Uses Overwolf game info from Redux
 */
const GameStatusPanel: React.FC<{
    gameInfo: any;
}> = ({ gameInfo }) => {
    return (
        <div className="game-status-panel">
            <h4 className="panel-title">Game Status</h4>
            {gameInfo?.title
                ? <div className="game-active">
                    <div className="game-icon">🎮</div>
                    <div className="game-info">
                        <span className="game-title">{gameInfo.title}</span>
                        <span className="game-status">
                            <span className="status-dot status-dot--active" />
                            PLAYING
                        </span>
                    </div>
                </div>
                : <div className="game-inactive">
                    <div className="game-icon">😴</div>
                    <div className="game-info">
                        <span className="game-title">No Game Detected</span>
                        <span className="game-status">
                            <span className="status-dot status-dot--inactive" />
                            IDLE
                        </span>
                    </div>
                </div>
            }
        </div>
    );
};

/**
 * Live Activity Feed - Placeholder Data
 * TODO: Replace with real activity hooks when available
 */
const LiveActivityFeed: React.FC = () => {
    const [activities] = useState([
        { id: 1, user: "ProGamer23", action: "completed Apex Challenge", reward: "500 FUEL", time: "2m ago" },
        { id: 2, user: "NeonRush", action: "reached Level 45", reward: "1000 XP", time: "5m ago" },
        { id: 3, user: "ShadowHunter", action: "opened Epic Lootbox", reward: "Rare Skin", time: "8m ago" },
        { id: 4, user: "CyberNinja", action: "joined Elite Guild", reward: "2X Multiplier", time: "12m ago" },
        { id: 5, user: "QuantumGamer", action: "won Squad Battle", reward: "750 FUEL", time: "15m ago" },
    ]);

    return (
        <div className="live-activity-feed">
            <h4 className="panel-title">
                <span className="live-indicator">🔴</span>
                Live Activity
            </h4>
            <div className="activity-list">
                {activities.map(activity =>
                    <div key={activity.id} className="activity-item">
                        <div className="activity-content">
                            <span className="activity-user">{activity.user}</span>
                            <span className="activity-action">{activity.action}</span>
                        </div>
                        <div className="activity-meta">
                            <span className="activity-reward">{activity.reward}</span>
                            <span className="activity-time">{activity.time}</span>
                        </div>
                    </div>)}
            </div>
        </div>
    );
};

/**
 * Left Sidebar - Navigation and Quick Actions
 * Narrow column matching Cockpit 3 left panel
 */
const LeftSidebar: React.FC = () => {
    const navigationItems = [
        { icon: "🎮", label: "Games", active: true },
        { icon: "⚔️", label: "Guilds", active: false },
        { icon: "🎁", label: "Inventory", active: false },
        { icon: "📊", label: "Stats", active: false },
        { icon: "🏆", label: "Leaderboard", active: false },
    ];

    const quickActions = [
        { icon: "⚡", label: "Load Fuel" },
        { icon: "🎯", label: "New Quest" },
        { icon: "📺", label: "Stream" },
    ];

    return (
        <div className="left-sidebar">
            {/* Navigation Section */}
            <div className="sidebar-section">
                <h4 className="sidebar-title">Navigation</h4>
                <div className="nav-items">
                    {navigationItems.map((item, index) =>
                        <div key={index} className={`nav-item ${item.active ? "nav-item--active" : ""}`}>
                            <span className="nav-icon">{item.icon}</span>
                            <span className="nav-label">{item.label}</span>
                        </div>)}
                </div>
            </div>

            {/* Quick Actions Section */}
            <div className="sidebar-section">
                <h4 className="sidebar-title">Quick Actions</h4>
                <div className="quick-actions">
                    {quickActions.map((action, index) =>
                        <button key={index} className="quick-action-btn">
                            <span className="action-icon">{action.icon}</span>
                            <span className="action-label">{action.label}</span>
                        </button>)}
                </div>
            </div>

            {/* Status Indicator */}
            <div className="sidebar-section">
                <div className="sidebar-status">
                    <div className="status-dot" />
                    <span className="status-text">SYSTEMS ONLINE</span>
                </div>
            </div>
        </div>
    );
};

/**
 * Data Debug Panel - Shows Real Data is Connected
 * Will be removed in later commits, just for verification
 */
const DataDebugPanel: React.FC<{
    fuelBalance: string;
    accountLevel: any;
    gameInfo: any;
    challengesData: any;
    userProfile: any;
}> = ({ fuelBalance, accountLevel, gameInfo, challengesData, userProfile }) => {
    return (
        <div className="data-debug-panel">
            <h4 className="panel-title">🔍 Data Debug (Remove Later)</h4>
            <div className="debug-items">
                <div className="debug-item">
                    <strong>Fuel Balance:</strong> {fuelBalance || "Loading..."}
                </div>
                <div className="debug-item">
                    <strong>User Level:</strong> {accountLevel?.level || "Loading..."}
                </div>
                <div className="debug-item">
                    <strong>XP Gained:</strong> {accountLevel?.experienceGainedAtThisLevel || 0}
                </div>
                <div className="debug-item">
                    <strong>Current Game:</strong> {gameInfo?.title || "No game detected"}
                </div>
                <div className="debug-item">
                    <strong>Active Challenges:</strong> {challengesData?.challenges?.length || 0}
                </div>
                <div className="debug-item">
                    <strong>User Avatar:</strong> {userProfile?.user?.gravatar ? "✅ Loaded" : "❌ Missing"}
                </div>
            </div>
        </div>
    );
};

/**
 * CockpitV3 Inner Component - Contains the actual dashboard logic
 */
const CockpitV3Inner: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { savedTab, savedGameId } = usePlayPageState();
    const isMobile = useIsMobile();
    const deviceContext = useDeviceContext();
    const layoutPrefs = useLayoutPreference();

    // Get query client for cache invalidation
    const queryClient = useQueryClient();

    // Get gameInfo and Overwolf status from Redux store - MUST be before useEffect that uses them
    const gameInfo = useSelector((store: IStore) => store.metaStore.overwolfGameInfo);
    const isOnOverwolf = useSelector((store: IStore) => store.metaStore.isOnOverwolf);

    // Hook to detect tablet breakpoint for ProfileCard positioning
    const [isTabletBreakpoint, setIsTabletBreakpoint] = useState(false);

    // Compact mode tab state for switching between quests and game views
    const [compactTab, setCompactTab] = useState<"quests" | "game">("game");

    useEffect(() => {
        const checkBreakpoint = () => {
            // Always use tablet layout in Overwolf (1260px width) or normal tablet breakpoints
            const isTablet = isOnOverwolf || window.innerWidth >= 768 && window.innerWidth < 1280;
            setIsTabletBreakpoint(isTablet);
        };

        checkBreakpoint();
        window.addEventListener("resize", checkBreakpoint);
        return () => window.removeEventListener("resize", checkBreakpoint);
    }, [isOnOverwolf]); // Re-check when Overwolf status changes

    // Check gameInfo structure
    useEffect(() => {
        if (gameInfo) {
            // gameInfo is available
        }
    }, [gameInfo]);

    // State for selected game from search bar
    const [selectedGame, setSelectedGame] = useState<OWGameResponse | null>(null);
    const [autoSelectAttempted, setAutoSelectAttempted] = useState(false);

    // Navigation is already declared above
    const location = useLocation();

    // Determine current view from URL
    const urlParams = new URLSearchParams(location.search);
    const currentView = urlParams.get("view");
    const gameSlugFromUrl = urlParams.get("game");

    // State for showing full guild leaderboard - now based on URL
    const showFullGuildLeaderboard = currentView === "guilds";

    // Check if we're in game view
    const isGameView = currentView === "games" || !!gameSlugFromUrl;

    // Check for action icon views
    const isLootShopView = currentView === "lootshop";
    const isLootBoxView = currentView === "lootbox";
    const isCollectiblesView = currentView === "collectibles";

    // Function to update view and URL
    const setShowFullGuildLeaderboard = (show: boolean) => {
        if (show) {
            navigate("/play?view=guilds", { replace: false });
        } else {
            navigate("/play", { replace: false });
        }
    };

    // Fetch game from URL if provided
    const { game: gameFromUrl, isLoading: gameFromUrlLoading } = useGameBySlug(gameSlugFromUrl);

    /*
     * Note: We don't use this for Overwolf game detection, only for search functionality
     * Keeping it undefined since GameSearchInline handles its own search
     */
    const allGames = undefined;

    // Load game from URL on mount or URL change
    useEffect(() => {
        if (gameFromUrl && !selectedGame) {
            setSelectedGame(gameFromUrl);
            setAutoSelectAttempted(true); // Mark as URL-loaded
        }
    }, [gameFromUrl, selectedGame]);

    // Auto-select game based on Overwolf detection (only if no URL game)
    useEffect(() => {
    // Use gameId which is what Overwolf actually provides
        const detectedGameId = gameInfo?.gameId?.toString();

        // Check if game has changed or we need initial selection
        if (detectedGameId && !gameSlugFromUrl) {
            // If game changed or no game selected yet (compare as strings to handle type mismatches)
            if (!selectedGame || String(selectedGame.gameId) !== String(detectedGameId)) {
                // Directly fetch the game by ID from the API
                fetch(`${getConfig().basePath}/overwolf/enhanced-game-info/${detectedGameId}`)
                    .then(response => {
                        if (!response.ok) {
                            throw new Error(`Game not found: ${detectedGameId} (status: ${response.status})`);
                        }
                        return response.json();
                    })
                    .then(game => {
                        if (game) {
                            setSelectedGame(game);
                            // Mark that we've selected this specific game
                            setAutoSelectAttempted(true);

                            // Invalidate React Query caches to force refetch
                            queryClient.invalidateQueries({ queryKey: ["game"] });
                            queryClient.invalidateQueries({ queryKey: ["game-stats"] });
                            queryClient.invalidateQueries({ queryKey: ["quest-stats"] });
                            queryClient.invalidateQueries({ queryKey: ["overwolf"] });
                        } else {
                            console.error("[CockpitV3] Game response was empty");
                        }
                    })
                    .catch(error => {
                        console.error("[CockpitV3] Failed to fetch game by ID:", error);
                        console.error("[CockpitV3] Error details:", error.message);
                        // Still mark as attempted to prevent infinite retries for this specific game
                        setAutoSelectAttempted(true);
                    });
            }
        }
    }, [gameInfo, selectedGame, gameSlugFromUrl, queryClient]); // React to any gameInfo changes

    /*
     * Don't reset autoSelectAttempted - this was causing infinite loops
     * Once we've attempted to select a game, don't try again unless the component remounts
     */

    /*
     * Fetch full game data when a game is selected
     * Use the slug as the key to ensure proper cache invalidation
     */
    const gameSlug = selectedGame?.slug || "";
    const { game: selectedGameData, loading: gameLoading } = useGameData(gameSlug);
    // State for game search modal
    const [showGameSearchModal, setShowGameSearchModal] = useState(false);

    // Modal states for ProfileCard interactions
    const [showLeaderboardModal, setShowLeaderboardModal] = useState(false);
    const [showLevelRewardsModal, setShowLevelRewardsModal] = useState(false);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [showLoadFuelModal, setShowLoadFuelModal] = useState(false);

    // Get real data from existing Play.tsx hooks
    const {
        fullToken,
        fuelBalance,
        isLoading,
        hasError,
        errors,
    } = usePlayPageData();

    // Redux selectors for user data
    const accountLevel = useSelector((store: IStore) => store.metaStore.accountLevel);
    const currentUser = useSelector((store: IStore) => store.metaStore.currentUser);

    // Get user profile data
    const { data: userProfile, refetch: refetchProfile } = useGetUserProfile(fullToken);

    // Get challenges data
    const {
        data: challengesData,
        isLoading: challengesLoading,
        error: challengesError,
        refetch: refetchChallenges,
    } = useGetActiveChallenges({
        limit: MAX_CHALLENGES_DISPLAY,
    });

    /**
     * POC: Centralized data fetching
     * Currently using existing hooks directly
     * FUTURE: Will move to repository pattern endpoints
     */

    // Me Column Data - User's personal information
    const {
        data: transactionsData,
        isLoading: transactionsLoading,
        refetch: refetchTransactions,
    } = useGetTransactionsPaginated(fullToken, currentUser?.user?.id, {
        limit: 10,
        offset: 0,
    });

    const {
        data: leaderboardRank,
        isLoading: leaderboardLoading,
        refetch: refetchLeaderboard,
    } = useGetUserLeaderboardRank(fullToken);

    const {
        data: overwolfSummary,
        isLoading: overwolfSummaryLoading,
        refetch: refetchOverwolfSummary,
    } = useGetOverwolfSummary();

    /*
     * Do Column Data - Activities and quests
     * (Challenges already fetched above)
     */

    // Fetch guild ownerships to get current guild
    const {
        data: guildOwnerships,
        isLoading: guildOwnershipLoading,
        refetch: refetchGuildOwnerships,
    } = useGetGuildOwnerships(fullToken, !!fullToken);

    // Us Column Data - Social and guild information
    const guildId = guildOwnerships?.membership?.guild || "";
    const currentGuild = guildOwnerships?.qualifiedGuilds?.find(guild => guild.id === guildId);

    // Fetch comprehensive guild progress data (includes guild info, user stats, members)
    const {
        data: guildProgress,
        isLoading: guildProgressLoading,
        refetch: refetchGuildProgress,
    } = useGetGuildProgress(guildId, fullToken, false, !!guildId && !!fullToken);

    // Fetch guild members for current month (keep for detailed member list if needed)
    const now = new Date();
    const currentMonthParam = `${now.getMonth() + 1},${now.getFullYear()},0`;
    const {
        data: guildMembersData,
        isLoading: guildMembersLoading,
    } = useGetCurrentGuildTopMembers(fullToken, currentMonthParam, !!fullToken && !!guildId);

    // Prepare consolidated data for each column
    const meColumnData = useMemo(() => ({
        userProfile,
        transactions: transactionsData,
        overwolfSummary: overwolfSummary,
        leaderboardRank: leaderboardRank?.rank,
        isLoading: isLoading || transactionsLoading || leaderboardLoading || overwolfSummaryLoading,
        error: undefined,
    }), [userProfile, transactionsData, overwolfSummary, leaderboardRank, isLoading, transactionsLoading, leaderboardLoading, overwolfSummaryLoading]);

    const doColumnData = useMemo(() => ({
        challenges: challengesData?.challenges,
        quests: undefined, // TODO: Add quest data
        missions: undefined, // TODO: Add mission data
        currentGame: selectedGameData,
        isLoading: challengesLoading || gameLoading,
        error: challengesError,
    }), [challengesData, selectedGameData, challengesLoading, gameLoading, challengesError]);

    const usColumnData = useMemo(() => ({
        guild: guildProgress?.guild,
        guildMembers: guildMembersData?.members,
        leaderboard: undefined, // TODO: Add full leaderboard
        socialFeed: undefined, // TODO: Add social feed
        isLoading: guildProgressLoading || guildMembersLoading,
        error: undefined,
    }), [guildProgress, guildMembersData, guildProgressLoading, guildMembersLoading]);

    // Refetch functions
    const refetchAll = useCallback(() => {
        refetchProfile();
        refetchTransactions();
        refetchLeaderboard();
        refetchChallenges();
        refetchGuildOwnerships();
        refetchGuildProgress();
    }, [refetchProfile, refetchTransactions, refetchLeaderboard, refetchChallenges, refetchGuildOwnerships, refetchGuildProgress]);

    const refetchMe = useCallback(() => {
        refetchProfile();
        refetchTransactions();
        refetchLeaderboard();
    }, [refetchProfile, refetchTransactions, refetchLeaderboard]);

    const refetchDo = useCallback(() => {
        refetchChallenges();
    }, [refetchChallenges]);

    const refetchUs = useCallback(() => {
        refetchGuildProgress();
    }, [refetchGuildProgress]);

    // Network-aware retry system for graceful wake-up handling
    const allRefetchFunctions = useMemo(() => [
        refetchProfile,
        refetchTransactions,
        refetchLeaderboard,
        refetchChallenges,
        refetchGuildOwnerships,
        refetchGuildProgress,
        refetchOverwolfSummary,
    ], [refetchProfile, refetchTransactions, refetchLeaderboard, refetchChallenges, refetchGuildOwnerships, refetchGuildProgress, refetchOverwolfSummary]);

    const networkRetry = useNetworkAwareRetry(allRefetchFunctions);
    const networkState = useNetworkState();

    // Format challenges data (same as Play.tsx)
    const formattedChallenges = useMemo<FormattedChallenge[]>(() => {
        if (!challengesData?.challenges) return [];

        return (challengesData.challenges || [])
            .filter(challenge => challenge.isActive === true)
            .map(challenge => ({
                id: challenge.id,
                name: challenge.name,
                cardTitle: challenge.cardTitle,
                description: challenge.description || "",
                brandLogo: challenge.brandLogo,
                brandColor: challenge.brandColor || "#aa96ef",
                brandMapSlug: challenge.brandMapSlug,
                backgroundImageLarge: challenge.backgroundImageLarge || challenge.imageUrl,
                backgroundImageSmall: challenge.backgroundImageSmall || challenge.imageUrl,
                start: challenge.start || 0,
                end: challenge.end || 0,
                isAccepted: challenge.isAccepted,
                imageUrl: challenge.imageUrl || CHALLENGE_PLACEHOLDER_IMAGE,
                active: true,
            }));
    }, [challengesData]);

    // Get featured challenge (first active challenge)
    const featuredChallenge = formattedChallenges[0] || null;

    // Create ProfileCard component for reuse
    const ProfileCardComponent = () =>
        <div className="profile-card-wrapper">
            <ProfileCard
                fuelBalance={fuelBalance}
                userProfile={meColumnData.userProfile}
                accountLevel={accountLevel}
                overwolfSummary={null}
                isAuthenticated={!!fullToken}
                isLoading={meColumnData.isLoading}
                forceMobileView={false}
                noCardStyling={true}
                cockpitView={true}
                onLoginClick={() => setShowLoginModal(true)}
                onBadgeClick={() => setShowLeaderboardModal(true)}
                onLevelsClick={() => setShowLevelRewardsModal(true)}
                onLoadFuelClick={() => setShowLoadFuelModal(true)}
            />
        </div>
  ;

    // Allow access without authentication for feature teasers


    // Show loading state
    if (isLoading) {
        return <CockpitLoader />;
    }

    // Determine effective layout mode
    let effectiveMode = layoutPrefs.mode;
    if (layoutPrefs.mode === "auto") {
    // Auto mode determines layout based on context
        if (deviceContext.isInGame && deviceContext.platform === "overwolf" && layoutPrefs.compactOnFocus) {
            effectiveMode = "compact";
        } else if (isMobile) {
            effectiveMode = "standard"; // Mobile gets standard layout, not compact
        } else {
            effectiveMode = "standard"; // Default to standard for desktop
        }
    }

    // Show compact view only for compact mode
    if (effectiveMode === "compact") {
        return (
            <>
                <DevourTopNav text="DevourPlay" />
                <NetworkStatusIndicator
                    isReconnecting={networkState.isReconnecting}
                    isRefetching={networkRetry.isRefetching}
                    onRetry={networkRetry.retry}
                />
                <CompactProfileBar />

                <div className="sophisticated-dashboard context-in-game">
                    <InGameCompactView
                        challenges={formattedChallenges}
                        challengesLoading={challengesLoading}
                        fuelBalance={fuelBalance}
                        accountLevel={accountLevel}
                    />
                    {/* <LayoutSwitcher /> */} {/* Temporarily hidden */}
                </div>
            </>
        );
    }

    // Add layout mode class to dashboard
    const dashboardClasses = `sophisticated-dashboard layout-${effectiveMode}${currentView === "guilds" ? " guilds-page" : ""}${selectedGame ? " game-active" : ""}`;

    return (
        <CockpitDataProvider
            meData={meColumnData}
            doData={doColumnData}
            usData={usColumnData}
            refetchAll={refetchAll}
            refetchMe={refetchMe}
            refetchDo={refetchDo}
            refetchUs={refetchUs}
        >
            <DevourTopNav text="DevourPlay" />
            <NetworkStatusIndicator
                isReconnecting={networkState.isReconnecting}
                isRefetching={networkRetry.isRefetching}
                onRetry={networkRetry.retry}
            />

            {/* Show compact profile bar ONLY in mobile/compact views */}
            {isMobile &&
        <CompactProfileBar />
            }

            <MobileBottomNav />
            <div className={dashboardClasses}>
                <div className="dashboard-grid">

                    {/* ME Column - Personal Identity - Hide in tablet/Overwolf mode or when game is detected */}
                    {!isTabletBreakpoint && !(gameInfo && gameInfo.gameId && gameInfo.isGameRunning) &&
          <div className="me-column">
              {/* Profile Card - show in Me column only when NOT tablet breakpoint */}
              <ProfileCardComponent />

              {/* Action Icons - Quick access buttons */}
              <ActionIcons />

              {/* Stats Card */}
              <StatsCard fullToken={fullToken} userId={currentUser?.user?.id} />

              {/* Transaction History Card */}
              <TransactionHistoryCard fullToken={fullToken} userId={currentUser?.user?.id} />

              {/* Additional ME column cards will go here */}
          </div>
                    }

                    {/* DO Column - Action Hub */}
                    <div className="do-column">
                        {/* Show tab navigation when game is detected by Overwolf */}
                        {gameInfo && gameInfo.gameId && gameInfo.isGameRunning &&
            <CompactTabNavigation
                activeTab={compactTab}
                onTabChange={setCompactTab}
                currentGameName={gameInfo?.gameTitle || selectedGame?.name || "Game"}
            />
                        }

                        {/* Check for guild leaderboard view first (accessible from any state) */}
                        {showFullGuildLeaderboard
                        // Guild-specific content takes over entire middle column
                            ? <div className="game-content-full">
                                <div className="game-nav-bar">
                                    <button
                                        className="close-game-btn"
                                        onClick={() => {
                                            setShowFullGuildLeaderboard(false);
                                            // If in game mode, return to the appropriate tab
                                            if (gameInfo && gameInfo.gameId && gameInfo.isGameRunning) {
                                                // Stay in the current tab when returning from guilds
                                            }
                                        }}
                                        aria-label="Return to previous view"
                                    >
                                        <span className="back-text-desktop">
                                            {gameInfo && gameInfo.gameId && gameInfo.isGameRunning ? "Back to Game" : "Back to Dashboard"}
                                        </span>
                                        <span className="back-text-mobile">←</span>
                                    </button>
                                </div>

                                {/* Full Guilds Page Content */}
                                <GuildsCockpitPage />
                            </div>
                            : isLootShopView
                            // LootShop view
                                ? <div className="game-content-full">
                                    <div className="game-nav-bar">
                                        <button
                                            className="close-game-btn"
                                            onClick={() => navigate("/play", { replace: false })}
                                            aria-label="Return to dashboard"
                                        >
                                            <span className="back-text-desktop">Back to Dashboard</span>
                                            <span className="back-text-mobile">←</span>
                                        </button>
                                    </div>
                                    <Suspense fallback={<CockpitLoader />}>
                                        <RestaurantPage />
                                    </Suspense>
                                </div>
                                : isLootBoxView
                                // LootBox view
                                    ? <div className="game-content-full">
                                        <div className="game-nav-bar">
                                            <button
                                                className="close-game-btn"
                                                onClick={() => navigate("/play", { replace: false })}
                                                aria-label="Return to dashboard"
                                            >
                                                <span className="back-text-desktop">Back to Dashboard</span>
                                                <span className="back-text-mobile">←</span>
                                            </button>
                                        </div>
                                        <Suspense fallback={<CockpitLoader />}>
                                            <LootBoxPage hideNav={true} embedded={true} />
                                        </Suspense>
                                    </div>
                                    : isCollectiblesView
                                    // Collectibles/My Stash view
                                        ? <div className="game-content-full">
                                            <div className="game-nav-bar">
                                                <button
                                                    className="close-game-btn"
                                                    onClick={() => navigate("/play", { replace: false })}
                                                    aria-label="Return to dashboard"
                                                >
                                                    <span className="back-text-desktop">Back to Dashboard</span>
                                                    <span className="back-text-mobile">←</span>
                                                </button>
                                            </div>
                                            <Suspense fallback={<CockpitLoader />}>
                                                <CollectiblesPage hideNav={true} />
                                            </Suspense>
                                        </div>
                                        : gameInfo && gameInfo.gameId && gameInfo.isGameRunning
                                        // We're in game detection mode - switch content based on active tab
                                            ? compactTab === "game"
                                            // Game tab is active - show game content for the detected game
                                                ? selectedGame
                                                // Show game content immediately when we have selectedGame

                                                    ? <div className="game-content-full" key={`game-content-${selectedGame?.gameId}`}>
                                                        {/* No navigation bar in compact game mode - users switch via tabs */}

                                                        {/* Game Hero Section (without ProfileCard and description in game tab) */}
                                                        <div className="game-hero-wrapper">
                                                            <GameHeroSection
                                                                game={selectedGame}
                                                                userStats={null}
                                                                hideProfileCard={true}
                                                                hideDescription={true} // Always hide in game tab view
                                                                key={`hero-${selectedGame?.gameId}`} // Force re-render on game change
                                                            />
                                                        </div>

                                                        {/* Mission Tracker / Quests */}
                                                        <div className="game-section">
                                                            <GameQuestSection game={selectedGame} hideSearch={true} compact={true} key={`quest-${selectedGame?.gameId}`} />
                                                        </div>

                                                        {/* Game News */}
                                                        <div className="game-section">
                                                            <GameNewsSection game={selectedGame} key={`news-${selectedGame?.gameId}`} />
                                                        </div>

                                                        {/* Game Info / About */}
                                                        <div className="game-section">
                                                            <GameAboutSection game={selectedGame} key={`about-${selectedGame?.gameId}`} />
                                                        </div>
                                                    </div>


                                                // Game is detected but not matched yet
                                                    : <div className="game-content-full">
                                                        <div className="game-loading">
                                                            <div>Loading game with ID: {gameInfo?.gameId}</div>
                                                            <div style={{ fontSize: "0.875rem", marginTop: "0.5rem", opacity: 0.7 }}>
                                                                Fetching game information...
                                                            </div>
                                                        </div>
                                                    </div>

                                                :
                                            // Quests & Stats tab is active - show stats content
                                                <>
                                                    {/* Quest Progress Card - Main focus */}
                                                    <QuestProgressCard compact={true} />

                                                    {/* Stats Cards Row */}
                                                    <div className="bottom-cards">
                                                        {/* My Stats Card - No wrapper needed, has its own container */}
                                                        <StatsCard fullToken={fullToken} userId={currentUser?.user?.id} />

                                                        {/* Last Session Card */}
                                                        <div className="data-card">
                                                            <LastSessionCard />
                                                        </div>
                                                    </div>
                                                </>

                                            : selectedGame
                                            // Manual game selection (not Overwolf detection) - existing behavior
                                                ? <div className="game-content-full">
                                                    <div className="game-nav-bar">
                                                        <button
                                                            className="close-game-btn"
                                                            onClick={() => {
                                                                navigate("/play", { replace: false });
                                                                setSelectedGame(null);
                                                            }}
                                                            aria-label="Return to dashboard"
                                                        >
                                                            <span className="back-text-desktop">Back to Dashboard</span>
                                                            <span className="back-text-mobile">←</span>
                                                        </button>

                                                        {/* Compact Game Search */}
                                                        <div style={{ flex: 1, minWidth: 0 }}>
                                                            <GameSearchInline
                                                                onGameSelect={(game) => {
                                                                    // Prevent flash by only updating if different game
                                                                    if (game?.slug !== selectedGame?.slug) {
                                                                        const gameSlug = game.slug || game.name.toLowerCase().replace(/\s+/g, "-");
                                                                        navigate(`/play?view=games&game=${gameSlug}`, { replace: false });
                                                                        setSelectedGame(game);
                                                                        setAutoSelectAttempted(true); // Mark as manually selected
                                                                    }
                                                                }}
                                                                selectedGame={selectedGame}
                                                                compact={true}
                                                            />
                                                        </div>
                                                    </div>

                                                    {/* Game Hero Section (without ProfileCard) */}
                                                    <div className="game-hero-wrapper">
                                                        <GameHeroSection game={selectedGameData || selectedGame} userStats={null} hideProfileCard={true} />
                                                    </div>

                                                    {/* Mission Tracker / Quests */}
                                                    <div className="game-section">
                                                        <GameQuestSection game={selectedGameData || selectedGame} hideSearch={true} />
                                                    </div>

                                                    {/* Leaderboard - Commented out for beta testing */}
                                                    {/* <div className="game-section">
                <GameLeaderboardSection game={selectedGameData || selectedGame} />
              </div> */}

                                                    {/* Game News */}
                                                    <div className="game-section">
                                                        <GameNewsSection game={selectedGameData || selectedGame} />
                                                    </div>

                                                    {/* Game Info / About */}
                                                    <div className="game-section">
                                                        <GameAboutSection game={selectedGameData || selectedGame} />
                                                    </div>
                                                </div>
                                                :
                                            // Default view when no game or guild page is selected
                                                <>
                                                    {/* Hero Section - Two columns */}
                                                    <div className="hero-section">
                                                        {/* Active Mission Card - Left Column (68% width) */}
                                                        <div className="hero-card">
                                                            <ActiveMissionCard
                                                                challenges={formattedChallenges}
                                                                isLoading={challengesLoading}
                                                            />
                                                        </div>

                                                        {/* Rotating Action Cards - Right Column (30% width) */}
                                                        <div className="hero-stats">
                                                            <RotatingActionCards />
                                                        </div>
                                                    </div>

                                                    {/* Game Search - "What are you playing today?" */}
                                                    <GameSearchInline
                                                        onGameSelect={(game) => {
                                                            // Use slug for cleaner URLs
                                                            const gameSlug = game.slug || game.name.toLowerCase().replace(/\s+/g, "-");
                                                            navigate(`/play?view=games&game=${gameSlug}`, { replace: false });
                                                            setSelectedGame(game);
                                                            setAutoSelectAttempted(true); // Mark as manually selected
                                                        }}
                                                        selectedGame={selectedGame}
                                                    />

                                                    {/* Bottom Cards Row */}
                                                    <div className="bottom-cards">

                                                        {/* Last Session Card - Shows actual last game played */}
                                                        <div className="data-card">
                                                            <LastSessionCard />
                                                        </div>

                                                        {/* Active Quests Summary Card */}
                                                        <div className="data-card">
                                                            <ActiveQuestsCard
                                                                onViewAllClick={() => {
                                                                    // Scroll to quest table
                                                                    const questTable = document.querySelector(".actions-table");
                                                                    questTable?.scrollIntoView({ behavior: "smooth" });
                                                                }}
                                                            />
                                                        </div>

                                                    </div>

                                                    {/* Quest Progress Table - All user quests across all games */}
                                                    <QuestProgressCard compact={isMobile} />

                                                    {/* ME Column cards moved here when in tablet/Overwolf mode - AFTER Quest Progress */}
                                                    {isTabletBreakpoint &&
            <div className="me-column-relocated">
                {/* Stats Card - moved from ME column */}
                <StatsCard fullToken={fullToken} userId={currentUser?.user?.id} />

                {/* Transaction History Card - moved from ME column */}
                <TransactionHistoryCard fullToken={fullToken} userId={currentUser?.user?.id} />
            </div>
                                                    }
                                                </>
                        }

                    </div>

                    {/* US Column - Community & Social */}
                    <div className="us-column">
                        {/* Profile Card - show at top of Us column when tablet breakpoint (Overwolf 1200px) */}
                        {isTabletBreakpoint && <ProfileCardComponent />}

                        {/* Show Steam Stats when game is selected */}
                        {selectedGame &&
            <div className="steam-stats-panel">
                <GameSteamStatsSection game={selectedGameData || selectedGame} />
            </div>
                        }

                        {/* Guild Leaderboard with integrated guild info - hide on mobile when in guilds view */}
                        {!(isMobile && showFullGuildLeaderboard) &&
            <GuildsLeaderboardCard
                fullToken={fullToken}
                userId={currentUser?.user?.id}
                guildId={guildId}
                onViewAll={() => setShowFullGuildLeaderboard(true)}
                guild={currentGuild}
                guildProgress={guildProgress}
                guildMembersData={guildMembersData}
                membersAroundUser={guildOwnerships?.membersAroundUser}
            />
                        }

                        {/* DevourPlay Discord Chat */}
                        <DevourPlayChat
                            fullToken={fullToken}
                            discordLinked={!!currentUser?.user?.discord?.id}
                            currentUser={currentUser}
                        />

                        {/* Community Events - Placeholder for future release */}
                        {/* <div className="flash-challenge-card">
            <div className="tournament-icon">⚡</div>
            <div className="tournament-info">
              <div>Flash Challenge Live!</div>
              <div className="premium-text">Get 3 kills in <span>next match</span></div>
            </div>
          </div> */}

                        {/* Community Stats - Placeholder for future release */}
                        {/* <div className="community-power-card">
            <h4>Community Power</h4>
            <div className="balance-amount">125,450</div>
            <div className="balance-controls">
              <button>🏆</button>
            </div>
            <h4>Total Guild XP</h4>
          </div> */}

                    </div>

                </div>
                {/* <LayoutSwitcher /> */} {/* Temporarily hidden */}
            </div>

            {/* Game Search Modal */}
            <GameSearchModal
                isOpen={showGameSearchModal}
                onClose={() => setShowGameSearchModal(false)}
            />

            {/* Leaderboard Modal */}
            <GoVipLeaderboardsModal
                isOpen={showLeaderboardModal}
                onClose={() => setShowLeaderboardModal(false)}
                leaderboardRank={meColumnData.leaderboardRank || 0}
                shouldShowUserRank={true}
            />

            {/* Level Rewards Modal */}
            <GoVipLevelsModal
                isOpen={showLevelRewardsModal}
                toggle={() => setShowLevelRewardsModal(false)}
            />

            {/* Login Modal */}
            <LoginModal
                isOpen={showLoginModal}
                onClose={() => setShowLoginModal(false)}
            />

            {/* Load Fuel Modal */}
            <LoadFuelModal
                isOpen={showLoadFuelModal}
                toggle={() => setShowLoadFuelModal(false)}
                size="xs2"
            />
        </CockpitDataProvider>
    );
};

/**
 * CockpitV3 - Wrapper with Layout Preference Provider
 */
const CockpitV3: React.FC = () => {

    return (
        <LayoutPreferenceProvider>
            <CockpitV3Inner />
        </LayoutPreferenceProvider>
    );
};

export default CockpitV3;